import { computed, Injectable, signal } from '@angular/core';
import Swiper, { Navigation, Pagination } from 'swiper';
import { StepperConfig } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class FormStepperService {
  // Signals - state management
  private _currentSlideIndex = signal(0);
  public currentSlideIndex = this._currentSlideIndex.asReadonly();
  
  private _totalSlides = signal(0);
  public totalSlides = this._totalSlides.asReadonly();
  
  private _isNextButtonClicked = signal(false);
  public isNextButtonClicked = this._isNextButtonClicked.asReadonly();
  
  private _isLastSlideValidationActive = signal(false);
  public isLastSlideValidationActive = this._isLastSlideValidationActive.asReadonly();
  
  private _shouldSubmitForm = signal(false);
  public shouldSubmitForm = this._shouldSubmitForm.asReadonly();
  
  // Computed signals
  public canGoPrev = computed(() => this._currentSlideIndex() > 0);
  public isLastSlide = computed(() => {
    const total = this._totalSlides();
    const current = this._currentSlideIndex();
    return total > 0 && current >= total - 1;
  });
  
  private _swiperInstance: Swiper | null = null;
  
  // Setters for signals
  setIsNextButtonClicked(value: boolean): void {
    this._isNextButtonClicked.set(value);
  }
  
  setIsLastSlideValidationActive(value: boolean): void {
    this._isLastSlideValidationActive.set(value);
  }

  triggerFormSubmission(): void {
    this._shouldSubmitForm.set(true);
  }

  resetFormSubmissionTrigger(): void {
    this._shouldSubmitForm.set(false);
  }

  // Swiper control methods
  initSwiper(element: HTMLElement, config: StepperConfig = {}): void {
    if (this._swiperInstance) {
      this.destroySwiper();
    }

    const swiperConfig = {
      modules: [Navigation, Pagination],
      slidesPerView: 1,
      allowTouchMove: config.allowTouchMove ?? false,
      loop: config.loopMode ?? false,
      navigation: false,
      
      pagination: config.showPagination ? {
        el: '.swiper-pagination',
        clickable: false,
        type: 'bullets' as const,
      } : false,
      
      on: {
        slideChange: (swiper: Swiper) => {
          this._currentSlideIndex.set(swiper.activeIndex);
        },
        init: (swiper: Swiper) => {
          this._totalSlides.set(swiper.slides.length);
        }
      }
    };
    
    this._swiperInstance = new Swiper(element, swiperConfig);
  }

  goToNext(): boolean {
    if (!this._swiperInstance) return false;
    this._swiperInstance.slideNext();
    return true;
  }

  goToPrev(): boolean {
    if (!this._swiperInstance) return false;
    this._swiperInstance.slidePrev();
    return true;
  }

  goToSlide(index: number): boolean {
    if (!this._swiperInstance) return false;
    this._swiperInstance.slideTo(index);
    return true;
  }

  destroySwiper(): void {
    if (this._swiperInstance) {
      this._swiperInstance.destroy(true, true);
      this._swiperInstance = null;
    }
    this._currentSlideIndex.set(0);
    this._totalSlides.set(0);
  }

  resetToFirstSlide(): void {
    this._currentSlideIndex.set(0);
    if (this._swiperInstance) {
      this._swiperInstance.slideTo(0);
    }
  }
}
